import React from 'react';
import { colorWithAlpha, mixColors } from './liquidShared';
import './WaterNetworkOverlay.css';

const WaterNetworkOverlay = ({
	width,
	height,
	connections = [],
}) => {
	if (!Array.isArray(connections) || connections.length === 0) {
		return null;
	}

	const overlayWidth = Number(width);
	const overlayHeight = Number(height);
	if (!(overlayWidth > 0) || !(overlayHeight > 0)) {
		return null;
	}

	return (
		<div
			className="water-network-overlay"
			aria-hidden="true"
			style={{
				width: `${overlayWidth}px`,
				height: `${overlayHeight}px`,
			}}
		>
			{connections.map((connection) => {
				const waterBase = String(connection?.waterColor || 'rgba(0, 173, 239, 0.52)').trim();
				const shellBase = String(connection?.shellColor || 'rgba(104, 145, 172, 0.72)').trim();
				const active = Boolean(connection?.active);
				const flowStrength = Math.max(0, Math.min(1, Number(connection?.flowStrength || 0)));
				const inset = Math.max(
					3,
					Math.min(
						connection?.orientation === 'vertical'
							? Number(connection?.width || 0)
							: Number(connection?.height || 0),
						14,
					) * 0.18,
				);

				return (
					<div
						key={connection.id}
						className={[
							'water-network-connector',
							`water-network-connector--${connection.orientation}`,
							active ? 'is-flowing' : 'is-idle',
						].join(' ')}
						style={{
							left: `${Number(connection?.x || 0)}px`,
							top: `${Number(connection?.y || 0)}px`,
							width: `${Number(connection?.width || 0)}px`,
							height: `${Number(connection?.height || 0)}px`,
							'--water-network-inset': `${inset}px`,
							'--water-network-flow-strength': flowStrength.toFixed(3),
							'--water-network-wave-duration': `${Math.max(0.9, 2.05 - (flowStrength * 1.05))}s`,
							'--water-network-wave-secondary-duration': `${Math.max(1.2, 2.85 - (flowStrength * 1.2))}s`,
							'--water-network-flow-direction': (
								connection?.flowDirection === 'right-to-left'
								|| connection?.flowDirection === 'bottom-to-top'
							)
								? 'reverse'
								: 'normal',
							'--water-network-shell-base': mixColors(shellBase, 'var(--header-background)', 0.18),
							'--water-network-shell-highlight': colorWithAlpha(
								mixColors(shellBase, 'var(--part-color)', 0.28),
								0.88,
							),
							'--water-network-shell-shadow': colorWithAlpha(
								mixColors(shellBase, 'var(--shadow-black-soft)', 0.32),
								0.94,
							),
							'--water-network-shell-border': colorWithAlpha(
								mixColors(shellBase, 'var(--part-color)', 0.2),
								0.72,
							),
							'--water-network-shell-outline': colorWithAlpha(
								mixColors(shellBase, 'var(--part-color)', 0.26),
								0.16,
							),
							'--water-network-shell-glow': colorWithAlpha(
								mixColors(shellBase, waterBase, 0.18),
								active ? 0.22 : 0.1,
							),
							'--water-network-cavity-highlight': colorWithAlpha(
								mixColors(shellBase, 'var(--part-color)', 0.12),
								0.42,
							),
							'--water-network-cavity-base': colorWithAlpha(
								mixColors(shellBase, 'var(--header-background)', 0.32),
								0.78,
							),
							'--water-network-water-base': colorWithAlpha(
								mixColors(waterBase, 'var(--part-color)', 0.14),
								active ? 0.9 : 0.72,
							),
							'--water-network-water-shadow': colorWithAlpha(
								mixColors(waterBase, 'var(--shadow-black-soft)', 0.28),
								active ? 0.92 : 0.74,
							),
							'--water-network-water-highlight': colorWithAlpha(
								mixColors(waterBase, 'var(--part-color)', 0.34),
								active ? 0.72 : 0.42,
							),
							'--water-network-water-outline': colorWithAlpha(
								mixColors(waterBase, 'var(--part-color)', 0.22),
								active ? 0.4 : 0.22,
							),
							'--water-network-water-glow': colorWithAlpha(
								mixColors(waterBase, 'var(--part-color)', 0.18),
								active ? 0.28 : 0.12,
							),
						}}
					>
						<div className="water-network-connector__inner" />
						<div className="water-network-connector__water">
							<div className="water-network-connector__waterCurrent water-network-connector__waterCurrent--primary" />
							<div className="water-network-connector__waterCurrent water-network-connector__waterCurrent--secondary" />
							<div className="water-network-connector__waterFoam" />
						</div>
						<div className="water-network-connector__sheen" />
					</div>
				);
			})}
		</div>
	);
};

export default WaterNetworkOverlay;
